import { useNavigation } from '@react-navigation/native';
import { BaseText, Button, Meta, theme } from 'aimo-ui';
import { MenuItemWrapped } from 'components/account/menuItems/MenuComponents';
import DateTimePicker from 'components/common/DateTimePicker';
import { format } from 'date-fns';
import useOngoingChargingSession from 'hooks/useOngoingChargingSession';
import useOngoingParkingSession from 'hooks/useOngoingParkingSession';
import useUpdateParkingMutation from 'hooks/useUpdateParkingMutation';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dimensions } from 'react-native';
import { useParkingStore } from 'stores/parkingStore';
import { StyledWrapper } from 'styles/ContainerStyles';
import { RootNavProps } from 'types/navigation';
import { createAction } from 'utils/analytics';
import { chargingAllowedToStop } from 'utils/chargingUtils';
import { isWeb } from 'utils/deviceUtils';
import { testIdentifiers } from 'utils/testIdentifiers';
import Icon from '../icons/Icon';
import { useUiStore } from 'stores/uiStore';

const { width } = Dimensions.get('window');

const StopParkingConfirmAnprManual: React.FC = () => {
  const { t } = useTranslation();
  const { navigate } = useNavigation<RootNavProps>();
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const { ongoingChargingSession } = useOngoingChargingSession();

  const { ongoingParkingSession: parkingSession } = useOngoingParkingSession();

  const startDate = useMemo(
    () =>
      parkingSession?.startDate
        ? new Date(parkingSession.startDate)
        : undefined,
    [parkingSession?.startDate]
  );
  const [calendarVisible, setCalendarVisible] = useState(false);

  const callback = (sessionIdString: string) => {
    navigate('parkingReceipt', {
      sessionId: sessionIdString,
      chargingSessionId: getChargingSessionId(),
    });
  };
  const { sessionId } = useParkingStore((state) => state.ongoingParkingSession);

  const getChargingSessionId = () => {
    if (chargingCanBeStopped) {
      return ongoingChargingSession?.chargingSessionId ?? '';
    }
  };
  const chargingCanBeStopped = chargingAllowedToStop(
    ongoingChargingSession?.status ?? 'IN_PROGRESS'
  );
  const { updateParking, updateParkingLoading } = useUpdateParkingMutation({
    callback,
    sessionId,
    actionType: 'STOP',
    isModal: true,
    endDate,
    anprManualStop:
      parkingSession?.type === 'ANPR' &&
      !!parkingSession?.parkZone?.parkingMethods?.manualStop,
  });

  const handleStopParking = async () => {
    updateParking();
    createAction('TAP', 'END_PARKING_SESSION');
  };

  const renderContent = () => {
    return (
      <>
        <StyledWrapper mb={60}>
          <StyledWrapper mb={25} alignItems="center">
            <Meta
              center
              color={theme.colors.gray300}
              testID="stop-ongoing-parking-confirm-description">
              {t('parking.anprInstructionOne')}
            </Meta>
            <Meta
              center
              style={{ marginTop: 15 }}
              color={theme.colors.gray300}
              testID="stop-ongoing-parking-confirm-description">
              {t('parking.anprInstructionTwo')}
            </Meta>
          </StyledWrapper>
          {parkingSession?.type === 'ANPR' && (
            <StyledWrapper>
              <MenuItemWrapped
                menuLabel={t('parking.anprStopTime')}
                iconName="Calendar-Outlined"
                testID="notifications-link"
                borderTopWidth={isWeb ? 1 : 3}
                onPress={() => {
                  setCalendarVisible(true);
                }}
                rightComponent={
                  endDate ? (
                    <BaseText color={theme.colors.gray300}>
                      {format(endDate, 'dd.MM.yyyy HH:mm')}
                    </BaseText>
                  ) : (
                    <Icon color={theme.colors.aimoUiRed} name="Info-Outlined" />
                  )
                }
              />
            </StyledWrapper>
          )}
          <DateTimePicker
            isVisible={calendarVisible}
            date={endDate ?? new Date()}
            minDate={startDate}
            maxDate={new Date()}
            mode="datetime"
            onConfirm={(date) => {
              setEndDate(new Date(date));
              setCalendarVisible(false);
            }}
            onCancel={() => setCalendarVisible(false)}
          />
        </StyledWrapper>

        <StyledWrapper center maxWidth={width} mb={20}>
          <Button
            disabled={parkingSession?.type === 'ANPR' && !endDate}
            size="l"
            {...testIdentifiers('stop-ongoing-parking-button')}
            text={t(
              updateParkingLoading
                ? 'parking.stoppingParking'
                : 'parking.stopParking'
            )}
            onPress={handleStopParking}
            loading={updateParkingLoading}
          />
        </StyledWrapper>
      </>
    );
  };

  return (
    <StyledWrapper mb={0}>
      {renderContent()}
      <StyledWrapper center maxWidth={width} mb={0}>
        <Button
          size="l"
          testID="cancel-stop-ongoing-parking"
          onPress={() => useUiStore.getState().setSheetModal('')}
          buttonType="tertiary"
          text={t('button.cancel')}
        />
      </StyledWrapper>
    </StyledWrapper>
  );
};

export default StopParkingConfirmAnprManual;
